import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { tournamentAPI } from '../services/api';
import {
  getAllPhases,
  getPhaseIndex,
  getPhaseLabel,
  getNextPhase,
  getPreviousPhase,
} from '../utils/tournamentPhases';
import './TournamentPortalManager.css';

function TournamentPortalManager() {
  const { tournamentId } = useParams();
  const { tournament, setTournament, setOnEditTournament } = useOutletContext();

  // Section toggle state
  const [openSections, setOpenSections] = useState({
    lifecycle: true,
    settings: false,
    activity: false,
    recap: false,
    requests: false,
    feedbackSummary: false,
  });

  // Lifecycle
  const [advancingPhase, setAdvancingPhase] = useState(false);
  const [confirmRewind, setConfirmRewind] = useState(false);

  // Settings
  const [settings, setSettings] = useState({
    golfGeniusUrl: '',
    postRoundFeedbackOpen: false,
    postRoundFeedbackDeadline: '',
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Posts (activity + recap)
  const [activityPosts, setActivityPosts] = useState([]);
  const [recapPosts, setRecapPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [postForm, setPostForm] = useState({ category: 'activity', title: '', body: '', links: [], pinned: false });
  const [editingPostId, setEditingPostId] = useState(null);
  const [savingPost, setSavingPost] = useState(false);

  // Roster requests
  const [requests, setRequests] = useState([]);
  const [requestPendingCount, setRequestPendingCount] = useState(0);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [requestFilter, setRequestFilter] = useState('all');
  const [respondingTo, setRespondingTo] = useState(null);
  const [tdResponse, setTdResponse] = useState('');

  // Disable edit tournament button for this tab
  useEffect(() => {
    if (setOnEditTournament) {
      setOnEditTournament(null);
    }
    return () => {
      if (setOnEditTournament) {
        setOnEditTournament(null);
      }
    };
  }, [setOnEditTournament]);

  // Initialize settings from tournament
  useEffect(() => {
    if (tournament) {
      setSettings({
        golfGeniusUrl: tournament.golfGeniusUrl || '',
        postRoundFeedbackOpen: tournament.postRoundFeedbackOpen || false,
        postRoundFeedbackDeadline: tournament.postRoundFeedbackDeadline
          ? new Date(tournament.postRoundFeedbackDeadline).toISOString().slice(0, 16)
          : '',
      });
    }
  }, [tournament]);

  // Load posts and requests on mount
  useEffect(() => {
    if (tournamentId && tournamentId !== 'demo') {
      loadPosts();
      loadRequests();
    }
  }, [tournamentId]);

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // ── Lifecycle ──────────────────────────────────────────

  const handleAdvancePhase = async () => {
    const next = getNextPhase(tournament?.portalStatus || 'signup');
    if (!next) return;

    try {
      setAdvancingPhase(true);
      const response = await tournamentAPI.update(tournamentId, { portalStatus: next });
      if (response.data) {
        setTournament(response.data);
      }
    } catch (err) {
      console.error('Error advancing phase:', err);
    } finally {
      setAdvancingPhase(false);
    }
  };

  const handleRewindPhase = async () => {
    const prev = getPreviousPhase(tournament?.portalStatus || 'signup');
    if (!prev) return;

    try {
      setAdvancingPhase(true);
      const response = await tournamentAPI.update(tournamentId, { portalStatus: prev });
      if (response.data) {
        setTournament(response.data);
      }
      setConfirmRewind(false);
    } catch (err) {
      console.error('Error rewinding phase:', err);
    } finally {
      setAdvancingPhase(false);
    }
  };

  // ── Settings ───────────────────────────────────────────

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      const updateData = {
        golfGeniusUrl: settings.golfGeniusUrl.trim() || null,
        postRoundFeedbackOpen: settings.postRoundFeedbackOpen,
        postRoundFeedbackDeadline: settings.postRoundFeedbackDeadline || null,
      };
      const response = await tournamentAPI.update(tournamentId, updateData);
      if (response.data) {
        setTournament(response.data);
      }
    } catch (err) {
      console.error('Error saving settings:', err);
    } finally {
      setSavingSettings(false);
    }
  };

  // ── Posts ──────────────────────────────────────────────

  const loadPosts = async () => {
    try {
      setLoadingPosts(true);
      const response = await tournamentAPI.getTournamentFeedback(tournamentId);
      // Posts endpoint is on tournament route
      const postsRes = await fetch(`/api/tournaments/${tournamentId}/posts`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (postsRes.ok) {
        const postsData = await postsRes.json();
        const allPosts = postsData.data?.posts || [];
        setActivityPosts(allPosts.filter((p) => p.category === 'activity'));
        setRecapPosts(allPosts.filter((p) => p.category === 'recap'));
      }
    } catch (err) {
      console.error('Error loading posts:', err);
    } finally {
      setLoadingPosts(false);
    }
  };

  const resetPostForm = (category = 'activity') => {
    setPostForm({ category, title: '', body: '', links: [], pinned: false });
    setEditingPostId(null);
  };

  const handleEditPost = (post) => {
    setPostForm({
      category: post.category,
      title: post.title,
      body: post.body,
      links: post.links || [],
      pinned: post.pinned || false,
    });
    setEditingPostId(post._id);
  };

  const handleSavePost = async () => {
    if (!postForm.title.trim() || !postForm.body.trim()) return;

    try {
      setSavingPost(true);
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3011';
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      };

      if (editingPostId) {
        await fetch(`${API_BASE}/api/tournaments/${tournamentId}/posts/${editingPostId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            title: postForm.title,
            body: postForm.body,
            links: postForm.links,
            pinned: postForm.pinned,
          }),
        });
      } else {
        await fetch(`${API_BASE}/api/tournaments/${tournamentId}/posts`, {
          method: 'POST',
          headers,
          body: JSON.stringify(postForm),
        });
      }

      resetPostForm(postForm.category);
      await loadPosts();
    } catch (err) {
      console.error('Error saving post:', err);
    } finally {
      setSavingPost(false);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;

    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3011';
      await fetch(`${API_BASE}/api/tournaments/${tournamentId}/posts/${postId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      await loadPosts();
    } catch (err) {
      console.error('Error deleting post:', err);
    }
  };

  const handleAddLink = () => {
    setPostForm((prev) => ({
      ...prev,
      links: [...prev.links, { label: '', url: '', type: 'external' }],
    }));
  };

  const handleUpdateLink = (index, field, value) => {
    setPostForm((prev) => ({
      ...prev,
      links: prev.links.map((link, i) => (i === index ? { ...link, [field]: value } : link)),
    }));
  };

  const handleRemoveLink = (index) => {
    setPostForm((prev) => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index),
    }));
  };

  // ── Roster Requests ────────────────────────────────────

  const loadRequests = async () => {
    try {
      setLoadingRequests(true);
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3011';
      const res = await fetch(`${API_BASE}/api/tournaments/${tournamentId}/roster-requests`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.data?.requests || []);
        setRequestPendingCount(data.data?.pendingCount || 0);
      }
    } catch (err) {
      console.error('Error loading requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleUpdateRequest = async (requestId, status, responseText = '') => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3011';
      const body = { status };
      if (responseText) body.tdResponse = responseText;

      await fetch(`${API_BASE}/api/tournaments/${tournamentId}/roster-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(body),
      });

      setRespondingTo(null);
      setTdResponse('');
      await loadRequests();
    } catch (err) {
      console.error('Error updating request:', err);
    }
  };

  const filteredRequests =
    requestFilter === 'all' ? requests : requests.filter((r) => r.status === requestFilter);

  // ── Render helpers ─────────────────────────────────────

  const currentPhase = tournament?.portalStatus || 'signup';
  const phases = getAllPhases();
  const currentIndex = getPhaseIndex(currentPhase);

  const renderPhaseUnlocks = (phaseId) => {
    const unlocks = {
      'signup': 'Activity tab visible to players',
      'roster-locked': 'Players can submit roster override requests',
      'tee-sheet-published': 'Players can view their tee time and group',
      'gameday': 'GolfGenius link shown on tee sheet tab',
      'completed': 'Post-round feedback form opens for players',
      'recap-posted': 'Recap tab becomes visible with results',
    };
    return unlocks[phaseId] || '';
  };

  const renderPostForm = (category) => {
    const isActive = postForm.category === category;
    if (!isActive && !editingPostId) {
      return (
        <button
          className="portal-btn portal-btn-primary"
          onClick={() => resetPostForm(category)}
        >
          + New {category === 'activity' ? 'Activity' : 'Recap'} Post
        </button>
      );
    }
    if (!isActive) return null;

    return (
      <div className="post-form">
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={postForm.title}
            onChange={(e) => setPostForm((prev) => ({ ...prev, title: e.target.value }))}
            placeholder="Post title..."
            maxLength={200}
          />
        </div>
        <div className="form-group">
          <label>Body</label>
          <textarea
            value={postForm.body}
            onChange={(e) => setPostForm((prev) => ({ ...prev, body: e.target.value }))}
            placeholder="Write your post..."
            rows={4}
            maxLength={5000}
          />
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={postForm.pinned}
              onChange={(e) => setPostForm((prev) => ({ ...prev, pinned: e.target.checked }))}
            />
            {' '}Pin to top
          </label>
        </div>

        <div className="form-group">
          <label>Links</label>
          {postForm.links.map((link, i) => (
            <div key={i} className="link-row">
              <input
                type="text"
                placeholder="Label"
                value={link.label}
                onChange={(e) => handleUpdateLink(i, 'label', e.target.value)}
              />
              <input
                type="url"
                placeholder="URL"
                value={link.url}
                onChange={(e) => handleUpdateLink(i, 'url', e.target.value)}
              />
              <select
                value={link.type}
                onChange={(e) => handleUpdateLink(i, 'type', e.target.value)}
              >
                <option value="external">External</option>
                <option value="results">Results</option>
                <option value="photos">Photos</option>
                <option value="golfgenius">GolfGenius</option>
              </select>
              <button className="portal-btn-icon" onClick={() => handleRemoveLink(i)}>
                x
              </button>
            </div>
          ))}
          <button className="portal-btn portal-btn-secondary" onClick={handleAddLink}>
            + Add Link
          </button>
        </div>

        <div className="form-actions">
          <button
            className="portal-btn portal-btn-secondary"
            onClick={() => resetPostForm(category)}
          >
            Cancel
          </button>
          <button
            className="portal-btn portal-btn-primary"
            onClick={handleSavePost}
            disabled={savingPost || !postForm.title.trim() || !postForm.body.trim()}
          >
            {savingPost ? 'Saving...' : editingPostId ? 'Update Post' : 'Publish Post'}
          </button>
        </div>
      </div>
    );
  };

  const renderPostList = (posts) => {
    if (posts.length === 0) {
      return <p className="empty-state">No posts yet</p>;
    }

    return (
      <div className="post-list">
        {posts.map((post) => (
          <div key={post._id} className={`post-card ${post.pinned ? 'pinned' : ''}`}>
            <div className="post-card-header">
              <h4>
                {post.pinned && <span className="pin-badge">Pinned</span>}
                {post.title}
              </h4>
              <span className="post-date">
                {new Date(post.publishedAt || post.createdAt).toLocaleDateString()}
              </span>
            </div>
            <p className="post-body">{post.body}</p>
            {post.links && post.links.length > 0 && (
              <div className="post-links">
                {post.links.map((link, i) => (
                  <a
                    key={i}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="post-link"
                  >
                    {link.label || link.url}
                  </a>
                ))}
              </div>
            )}
            <div className="post-card-actions">
              <button
                className="portal-btn portal-btn-secondary"
                onClick={() => handleEditPost(post)}
              >
                Edit
              </button>
              <button
                className="portal-btn portal-btn-danger"
                onClick={() => handleDeletePost(post._id)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (tournamentId === 'demo') {
    return (
      <div className="portal-manager">
        <div className="portal-section">
          <h2>Portal Management</h2>
          <p>Portal management is not available in demo mode.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-manager">
      {/* Section 1: Lifecycle Stepper */}
      <div className="portal-section">
        <div className="section-header" onClick={() => toggleSection('lifecycle')}>
          <h3>Portal Lifecycle</h3>
          <span className="section-toggle">{openSections.lifecycle ? '-' : '+'}</span>
        </div>
        {openSections.lifecycle && (
          <div className="section-body">
            <div className="phase-stepper">
              {phases.map((phase) => {
                const phaseIdx = phase.index;
                let stepClass = 'phase-step';
                if (phaseIdx < currentIndex) stepClass += ' completed';
                else if (phaseIdx === currentIndex) stepClass += ' current';
                else stepClass += ' upcoming';

                return (
                  <div key={phase.id} className={stepClass}>
                    <div className="phase-dot" />
                    <div className="phase-info">
                      <span className="phase-label">{phase.label}</span>
                      <span className="phase-unlock">{renderPhaseUnlocks(phase.id)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="lifecycle-actions">
              {getPreviousPhase(currentPhase) && (
                <>
                  {confirmRewind ? (
                    <div className="rewind-confirm">
                      <span>Rewind to {getPhaseLabel(getPreviousPhase(currentPhase))}?</span>
                      <button
                        className="portal-btn portal-btn-danger"
                        onClick={handleRewindPhase}
                        disabled={advancingPhase}
                      >
                        Confirm Rewind
                      </button>
                      <button
                        className="portal-btn portal-btn-secondary"
                        onClick={() => setConfirmRewind(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="portal-btn portal-btn-secondary"
                      onClick={() => setConfirmRewind(true)}
                    >
                      Rewind
                    </button>
                  )}
                </>
              )}

              {getNextPhase(currentPhase) && (
                <button
                  className="portal-btn portal-btn-primary"
                  onClick={handleAdvancePhase}
                  disabled={advancingPhase}
                >
                  {advancingPhase
                    ? 'Advancing...'
                    : `Advance to ${getPhaseLabel(getNextPhase(currentPhase))}`}
                </button>
              )}

              {!getNextPhase(currentPhase) && (
                <span className="phase-complete-badge">Tournament lifecycle complete</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Section 2: Settings */}
      <div className="portal-section">
        <div className="section-header" onClick={() => toggleSection('settings')}>
          <h3>Settings</h3>
          <span className="section-toggle">{openSections.settings ? '-' : '+'}</span>
        </div>
        {openSections.settings && (
          <div className="section-body">
            <div className="form-group">
              <label htmlFor="gg-url">GolfGenius URL</label>
              <input
                id="gg-url"
                type="url"
                value={settings.golfGeniusUrl}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, golfGeniusUrl: e.target.value }))
                }
                placeholder="https://www.golfgenius.com/..."
              />
              <small>Shown to players on the Tee Sheet tab during gameday</small>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.postRoundFeedbackOpen}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      postRoundFeedbackOpen: e.target.checked,
                    }))
                  }
                />
                {' '}Post-round feedback open
              </label>
            </div>

            <div className="form-group">
              <label htmlFor="feedback-deadline">Post-round feedback deadline</label>
              <input
                id="feedback-deadline"
                type="datetime-local"
                value={settings.postRoundFeedbackDeadline}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    postRoundFeedbackDeadline: e.target.value,
                  }))
                }
              />
              <small>Leave empty for no deadline</small>
            </div>

            <button
              className="portal-btn portal-btn-primary"
              onClick={handleSaveSettings}
              disabled={savingSettings}
            >
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}
      </div>

      {/* Section 3: Activity Posts */}
      <div className="portal-section">
        <div className="section-header" onClick={() => toggleSection('activity')}>
          <h3>Activity Posts ({activityPosts.length})</h3>
          <span className="section-toggle">{openSections.activity ? '-' : '+'}</span>
        </div>
        {openSections.activity && (
          <div className="section-body">
            {renderPostForm('activity')}
            {renderPostList(activityPosts)}
          </div>
        )}
      </div>

      {/* Section 4: Recap Posts */}
      <div className="portal-section">
        <div className="section-header" onClick={() => toggleSection('recap')}>
          <h3>Recap Posts ({recapPosts.length})</h3>
          <span className="section-toggle">{openSections.recap ? '-' : '+'}</span>
        </div>
        {openSections.recap && (
          <div className="section-body">
            {renderPostForm('recap')}
            {renderPostList(recapPosts)}
          </div>
        )}
      </div>

      {/* Section 5: Player Requests */}
      <div className="portal-section">
        <div className="section-header" onClick={() => toggleSection('requests')}>
          <h3>
            Player Requests
            {requestPendingCount > 0 && (
              <span className="pending-badge">{requestPendingCount}</span>
            )}
          </h3>
          <span className="section-toggle">{openSections.requests ? '-' : '+'}</span>
        </div>
        {openSections.requests && (
          <div className="section-body">
            <div className="request-filters">
              {['all', 'submitted', 'acknowledged', 'applied', 'denied'].map((status) => (
                <button
                  key={status}
                  className={`filter-chip ${requestFilter === status ? 'active' : ''}`}
                  onClick={() => setRequestFilter(status)}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                  {status === 'submitted' && requestPendingCount > 0 && (
                    <span className="chip-count">{requestPendingCount}</span>
                  )}
                </button>
              ))}
            </div>

            {loadingRequests ? (
              <p>Loading requests...</p>
            ) : filteredRequests.length === 0 ? (
              <p className="empty-state">No requests {requestFilter !== 'all' ? `with status "${requestFilter}"` : ''}</p>
            ) : (
              <div className="request-list">
                {filteredRequests.map((req) => (
                  <div key={req._id} className={`request-card status-${req.status}`}>
                    <div className="request-header">
                      <div className="request-player">
                        <strong>
                          {req.memberId?.firstName} {req.memberId?.lastName}
                        </strong>
                        <span className="request-ghin">GHIN: {req.memberGhin}</span>
                      </div>
                      <span className={`request-status-badge ${req.status}`}>{req.status}</span>
                    </div>

                    <div className="request-details">
                      {req.timePreference && req.timePreference !== 'no-preference' && (
                        <div className="request-field">
                          <span className="field-label">Time:</span> {req.timePreference}
                        </div>
                      )}
                      {req.partnerRequests && req.partnerRequests.length > 0 && (
                        <div className="request-field">
                          <span className="field-label">Partners:</span>{' '}
                          {req.partnerRequests.map((p) => p.name).join(', ')}
                        </div>
                      )}
                      {req.specialRequests && (
                        <div className="request-field">
                          <span className="field-label">Special:</span> {req.specialRequests}
                        </div>
                      )}
                    </div>

                    {req.tdResponse && (
                      <div className="td-response">
                        <span className="field-label">TD Response:</span> {req.tdResponse}
                      </div>
                    )}

                    <div className="request-actions">
                      {req.status === 'submitted' && (
                        <>
                          <button
                            className="portal-btn portal-btn-primary"
                            onClick={() => handleUpdateRequest(req._id, 'applied')}
                          >
                            Apply
                          </button>
                          <button
                            className="portal-btn portal-btn-secondary"
                            onClick={() => handleUpdateRequest(req._id, 'acknowledged')}
                          >
                            Acknowledge
                          </button>
                          <button
                            className="portal-btn portal-btn-danger"
                            onClick={() => {
                              setRespondingTo(req._id);
                              setTdResponse('');
                            }}
                          >
                            Deny
                          </button>
                        </>
                      )}
                      {req.status === 'acknowledged' && (
                        <>
                          <button
                            className="portal-btn portal-btn-primary"
                            onClick={() => handleUpdateRequest(req._id, 'applied')}
                          >
                            Apply
                          </button>
                          <button
                            className="portal-btn portal-btn-danger"
                            onClick={() => {
                              setRespondingTo(req._id);
                              setTdResponse('');
                            }}
                          >
                            Deny
                          </button>
                        </>
                      )}
                    </div>

                    {respondingTo === req._id && (
                      <div className="deny-form">
                        <textarea
                          value={tdResponse}
                          onChange={(e) => setTdResponse(e.target.value)}
                          placeholder="Reason for denial (optional)..."
                          rows={2}
                          maxLength={500}
                        />
                        <div className="form-actions">
                          <button
                            className="portal-btn portal-btn-secondary"
                            onClick={() => setRespondingTo(null)}
                          >
                            Cancel
                          </button>
                          <button
                            className="portal-btn portal-btn-danger"
                            onClick={() => handleUpdateRequest(req._id, 'denied', tdResponse)}
                          >
                            Confirm Deny
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="request-meta">
                      Submitted: {new Date(req.submittedAt || req.createdAt).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default TournamentPortalManager;
